import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';
import type { ParagraphDto } from './books';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface CreateParagraphInput {
  chapterId: string;
  contentHtml: string;
  sequenceOrder?: number;
  type?: string;
}

export function useCreateParagraph() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateParagraphInput) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/chapters/${input.chapterId}/paragraphs`, {
          json: {
            contentHtml: input.contentHtml,
            sequenceOrder: input.sequenceOrder,
            type: input.type ?? 'prose',
          },
        })
        .json<ApiResponse<ParagraphDto>>();
      return response.data;
    },
    onSuccess: (data) =>
      queryClient.invalidateQueries({
        queryKey: ['paragraphs', data.chapterId],
      }),
  });
}

export function useDeleteParagraph(chapterId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/paragraphs/${id}`);
    },
    onSuccess: () => {
      if (chapterId)
        queryClient.invalidateQueries({ queryKey: ['paragraphs', chapterId] });
    },
  });
}

export interface SplitParagraphResult {
  first: ParagraphDto;
  second: ParagraphDto;
}

export function useSplitParagraph(chapterId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      firstHtml: string;
      secondHtml: string;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/paragraphs/${input.id}/split`, {
          json: { firstHtml: input.firstHtml, secondHtml: input.secondHtml },
        })
        .json<ApiResponse<SplitParagraphResult>>();
      return response.data;
    },
    onSuccess: () => {
      if (chapterId)
        queryClient.invalidateQueries({ queryKey: ['paragraphs', chapterId] });
    },
  });
}

export function useMergeParagraphs(chapterId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sourceId: string; targetId: string }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/paragraphs/${input.sourceId}/merge`, {
          json: { targetId: input.targetId },
        })
        .json<ApiResponse<ParagraphDto>>();
      return response.data;
    },
    onSuccess: () => {
      if (chapterId)
        queryClient.invalidateQueries({ queryKey: ['paragraphs', chapterId] });
    },
  });
}

export function useReorderParagraphs(chapterId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      if (!chapterId) return;
      const client = createApiClient(getToken);
      await client.put(`api/v2/chapters/${chapterId}/paragraphs/reorder`, {
        json: { orderedIds },
      });
    },
    onSuccess: () => {
      if (chapterId)
        queryClient.invalidateQueries({ queryKey: ['paragraphs', chapterId] });
    },
  });
}
